import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

type Bucket = { count: number; revenue: number };
type Buckets = Record<string, Bucket>;
type PeriodRet = { renewed: number; churned: number; rate: number };
type Ret = PeriodRet & { by_period?: Record<string, PeriodRet> };

export type Forecast = {
  expiring: { proxy: Buckets; vpn: Buckets; total: Buckets };
  by_plan: { proxy: Record<string, number>; vpn: Record<string, number> };
  mrr: { proxy: number; vpn: number; total: number };
  retention: { proxy: Ret; vpn: Ret; churned_total: number; churn_cost: number };
  funnel: {
    users_total: number;
    buyers: number;
    conversion: number;
    mrr: number;
    avg_check: number;
  };
  trend: { weeks: number[]; change_pct: number; falling_streak: boolean };
};

type Stats = {
  users_today: number;
  users_this_month: number;
  users_total: number;
  subs_proxy_active: number;
  subs_proxy_trial: number;
  subs_any_active: number;
  today_proxy_paid?: number;
  today_vpn_paid?: number;
  today_trials?: number;
  subs_vpn_active: number;
  subs_vpn_remote: number;
  subs_vpn_trial?: number;
  revenue_total: number;
  revenue_week: number;
  revenue_today: number;
  revenue_total_stars: number;
  revenue_week_stars: number;
  revenue_proxy_total: number;
  revenue_vpn_total: number;
  revenue_proxy_stars: number;
  revenue_vpn_stars: number;
};

type Infra = {
  nodes: { total: number; up: number; degraded: number; offline: number };
  hosts: { total: number; up: number; degraded: number; down: number };
  conns: number;
};

/** Единый формат денег с ботом: «17 946 р», без валютного знака Intl
 * (он давал «17 946 ₽» с неразрывными пробелами — не совпадало с ботом). */
export function money(v: number, short = false): string {
  if (short && Math.abs(v) >= 1000) {
    const k = v / 1000;
    return `${k.toFixed(k >= 100 ? 0 : 1).replace(".", ",")}k р`;
  }
  return `${Math.round(v).toLocaleString("ru-RU")} р`;
}

const n = (v: number) => v.toLocaleString("ru-RU");

/** Порог, ниже которого продления считаются проблемой, а не просто цифрой. */
const RETENTION_ALARM = 50;
/** Доля живых нод, ниже которой парк считается лежащим. */
const FLEET_ALARM = 0.7;

const PLAN_LABEL: Record<string, string> = {
  "30": "1 мес",
  "90": "3 мес",
  "180": "6 мес",
  "365": "12 мес",
};

const PERIOD_LABEL: Record<string, string> = {
  month: "1 мес",
  quarter: "3 мес",
  long: "6–12 мес",
};

export function RevenueForecast() {
  const [open, setOpen] = useState(false);

  const f = useQuery<Forecast>({
    queryKey: ["stats-forecast"],
    queryFn: () => api<Forecast>("/api/admin/stats/forecast"),
    refetchInterval: 60_000,
  });
  const s = useQuery<Stats>({
    queryKey: ["stats"],
    queryFn: () => api<Stats>("/api/admin/stats"),
  });
  const infra = useQuery<Infra>({
    queryKey: ["infra-summary"],
    queryFn: () => api<Infra>("/api/admin/infra-summary"),
    refetchInterval: 8000,
  });

  if (f.isLoading || !f.data || !s.data) {
    return (
      <div className="space-y-3 mb-8">
        <div className="rounded-2xl card-elev h-28 animate-pulse" />
        <div className="rounded-2xl card-elev h-40 animate-pulse" />
      </div>
    );
  }

  const d = f.data;
  const st = s.data;
  const todo = buildTodo(d, infra.data);
  // Уникальные платники: юзер с прокси И VPN — один человек, не два.
  const subs = st.subs_any_active || st.subs_proxy_active + st.subs_vpn_active;
  // В панели живых больше, чем размечено в леджере (выданы/продлены мимо учёта).
  const vpnUnmarked = Math.max(0, (st.subs_vpn_remote || 0) - st.subs_vpn_active - (st.subs_vpn_trial ?? 0));
  const retTone =
    d.retention.proxy.rate < RETENTION_ALARM ? "text-rose-300" : "text-emerald-300";

  return (
    <div className="mb-8 space-y-5">
      {/* ── Три числа: всё остальное — детали ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Big
          label="Доход за месяц"
          value={money(d.mrr.total)}
          sub={`неделя ${money(st.revenue_week)} · сегодня ${money(st.revenue_today)}`}
        />
        <Big
          label="Подписчики"
          value={n(subs)}
          tone="text-brand"
          sub={`${n(st.subs_proxy_active)} прокси${
            st.subs_proxy_trial ? ` (+${n(st.subs_proxy_trial)} проб)` : ""
          } · ${n(st.subs_vpn_active)} VPN${
            st.subs_vpn_trial ? ` (+${n(st.subs_vpn_trial)} проб)` : ""
          }${vpnUnmarked ? ` · ${n(vpnUnmarked)} не размечено` : ""}`}
          foot={`сегодня: +${st.users_today} рег · +${st.today_proxy_paid ?? 0} прокси · +${st.today_vpn_paid ?? 0} VPN · +${st.today_trials ?? 0} проб`}
        />
        <Big
          label="Продления"
          value={`${d.retention.proxy.rate.toFixed(0)}%`}
          tone={retTone}
          sub={
            d.retention.churned_total > 0
              ? `уходит ${n(d.retention.churned_total)} чел. · −${money(d.retention.churn_cost)}/мес`
              : "оттока нет"
          }
        />
      </div>

      {/* ── Что сделать ── */}
      {todo.length > 0 && (
        <div>
          <Head hint={`${todo.length} ${plural(todo.length)}`}>Что сделать</Head>
          <div className="space-y-2">
            {todo.map((a) => (
              <TodoRow key={a.id} {...a} />
            ))}
          </div>
        </div>
      )}

      {/* ── Детали по клику ── */}
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full py-2.5 rounded-xl card-elev text-sm text-zinc-400 hover:text-brand hover:border-brand/40 transition-colors"
        >
          {open ? "Свернуть подробности ↑" : "Подробнее ↓"}
        </button>
        {open && <Details d={d} st={st} />}
      </div>
    </div>
  );
}

/* ─────────────── Детали ─────────────── */

function Details({ d, st }: { d: Forecast; st: Stats }) {
  const [tab, setTab] = useState<"proxy" | "vpn">("proxy");
  const isProxy = tab === "proxy";
  const exp = isProxy ? d.expiring.proxy : d.expiring.vpn;
  const plans = isProxy ? d.by_plan.proxy : d.by_plan.vpn;
  const mrr = isProxy ? d.mrr.proxy : d.mrr.vpn;
  const ret = isProxy ? d.retention.proxy : d.retention.vpn;
  const subs = isProxy ? st.subs_proxy_active : st.subs_vpn_active;
  const revenue = isProxy ? st.revenue_proxy_total : st.revenue_vpn_total;
  const stars = isProxy ? st.revenue_proxy_stars : st.revenue_vpn_stars;
  const planTotal = Object.values(plans).reduce((a, b) => a + b, 0);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-2">
        <TabBtn active={isProxy} onClick={() => setTab("proxy")}>
          Прокси
        </TabBtn>
        <TabBtn active={!isProxy} onClick={() => setTab("vpn")} tone="sky">
          VPN
        </TabBtn>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Small label="Подписчиков" value={n(subs)} />
        <Small label="MRR" value={money(mrr)} sub={`${share(mrr, d.mrr.total)}% от общего`} />
        <Small
          label="Средний чек"
          value={money(subs > 0 ? mrr / subs : 0)}
          sub="в месяц с подписчика"
        />
        <Small
          label="Заработано"
          value={money(revenue)}
          sub={stars ? `+ ${n(stars)} ⭐` : undefined}
        />
      </div>

      <div className="rounded-2xl card-elev p-5">
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mb-3">
          Заканчивается · {money(exp["30"]?.revenue ?? 0)} потенциала
        </div>
        <ExpiringBar b={exp} to={`/subscriptions?tab=${tab}`} />
      </div>

      {ret.by_period && (
        <div className="rounded-2xl card-elev p-5">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mb-3">
            Продления по сроку тарифа
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Object.keys(PERIOD_LABEL).map((k) => {
              const b = ret.by_period![k];
              const tot = b ? b.renewed + b.churned : 0;
              return (
                <div key={k}>
                  <div className="text-[11px] text-zinc-500">{PERIOD_LABEL[k]}</div>
                  <div
                    className={`text-xl font-bold tabular-nums mt-1 ${
                      !tot
                        ? "text-zinc-600"
                        : b.rate < RETENTION_ALARM
                          ? "text-rose-300"
                          : "text-emerald-300"
                    }`}
                  >
                    {tot ? `${b.rate.toFixed(0)}%` : "—"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 tabular-nums">
                    {tot ? `${b.renewed} из ${tot}` : "нет истёкших"}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[11.5px] text-zinc-600 mt-3">
            Суточные тарифы не считаются: это разовая проба, а не подписка.
          </div>
        </div>
      )}

      <div className="rounded-2xl card-elev p-5">
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mb-3">
          Активные по сроку тарифа · {n(planTotal)}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.keys(PLAN_LABEL).map((p) => {
            const v = plans[p] ?? 0;
            const w = planTotal > 0 ? (v / planTotal) * 100 : 0;
            return (
              <div key={p}>
                <div className="text-[11px] text-zinc-500">{PLAN_LABEL[p]}</div>
                <div className="text-xl font-bold tabular-nums mt-1">{n(v)}</div>
                <div className="h-1 rounded-full bg-white/[0.05] mt-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isProxy ? "bg-brand/70" : "bg-sky-400/70"}`}
                    style={{ width: `${w}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl card-elev p-5 flex gap-8 flex-wrap">
        <Pot label="Всего юзеров" value={n(d.funnel.users_total)} sub={`+${st.users_this_month} за месяц`} />
        <Pot
          label="Из них купили"
          value={n(d.funnel.buyers)}
          sub={`конверсия ${d.funnel.conversion.toFixed(1)}%`}
          tone="text-brand"
        />
        <Pot
          label="Звёзды"
          value={`${n(st.revenue_total_stars)} ⭐`}
          sub={`неделя ${n(st.revenue_week_stars)} ⭐`}
          tone="text-amber-300"
        />
      </div>
    </div>
  );
}

function ExpiringBar({ b, to }: { b: Buckets; to: string }) {
  // Корзины из API вложенные (30 включает 7 и 1). Разносим в непересекающиеся,
  // иначе «1447 за месяц» рядом со «179 за неделю» читается как несходящиеся
  // группы — именно на это и была жалоба.
  const b1 = b["1"]?.count ?? 0;
  const b7 = (b["7"]?.count ?? 0) - b1;
  const b30 = (b["30"]?.count ?? 0) - (b["7"]?.count ?? 0);
  const total = b["30"]?.count ?? 0;
  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  const sep = to.includes("?") ? "&" : "?";

  return (
    <>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-2xl font-bold tabular-nums tracking-tight">{n(total)}</span>
        <span className="text-zinc-500 text-sm">подписок в ближайший месяц</span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.05] mt-3 overflow-hidden flex">
        <div className="h-full bg-rose-400" style={{ width: `${pct(b1)}%` }} />
        <div className="h-full bg-amber-400" style={{ width: `${pct(b7)}%` }} />
        <div className="h-full bg-brand/50" style={{ width: `${pct(b30)}%` }} />
      </div>
      <div className="flex justify-between mt-2.5 text-[12px] tabular-nums flex-wrap gap-x-4">
        <Link to={`${to}${sep}expiring_days=1`} className="text-rose-300 hover:underline">
          {n(b1)} завтра
        </Link>
        <Link to={`${to}${sep}expiring_days=7`} className="text-amber-300 hover:underline">
          {n(b7)} за 2–7 дней
        </Link>
        <Link to={`${to}${sep}expiring_days=30`} className="text-zinc-500 hover:underline">
          {n(b30)} за 8–30 дней
        </Link>
      </div>
    </>
  );
}

function share(part: number, total: number): string {
  return total > 0 ? ((part / total) * 100).toFixed(0) : "0";
}

function plural(count: number): string {
  const last = count % 10;
  const two = count % 100;
  if (two >= 11 && two <= 14) return "пунктов";
  if (last === 1) return "пункт";
  if (last >= 2 && last <= 4) return "пункта";
  return "пунктов";
}

type Todo = {
  id: string;
  tone: "crit" | "warn";
  title: string;
  sub: string;
  money?: string;
  moneyTone?: string;
  action?: { label: string; to: string };
};

/** Дашборд = что требует действия. Строка появляется только когда есть проблема,
 * и ведёт на КОНКРЕТНЫЙ срез, а не на общую таблицу. */
function buildTodo(d: Forecast, infra?: Infra): Todo[] {
  const out: Todo[] = [];

  if (d.retention.proxy.rate < RETENTION_ALARM && d.retention.churned_total > 0) {
    out.push({
      id: "retention",
      tone: "crit",
      title: `Продления упали до ${d.retention.proxy.rate.toFixed(0)}%`,
      sub: `${n(d.retention.churned_total)} ушли за 30 дней · у VPN ${d.retention.vpn.rate.toFixed(0)}%`,
      money: `−${money(d.retention.churn_cost)}/мес`,
      moneyTone: "text-rose-300",
      action: { label: "Показать ушедших", to: "/subscriptions?churned_days=30" },
    });
  }

  if (infra && infra.nodes.total > 0 && infra.nodes.up / infra.nodes.total < FLEET_ALARM) {
    out.push({
      id: "fleet",
      tone: "crit",
      title: `Ноды: работают ${infra.nodes.up} из ${infra.nodes.total}`,
      sub: `хосты ${infra.hosts.up} из ${infra.hosts.total} · ${infra.nodes.degraded + infra.hosts.degraded} в деграде`,
      money: `${n(infra.conns)} коннектов`,
      moneyTone: "text-zinc-400",
      action: { label: "Открыть ноды", to: "/nodes" },
    });
  }

  const soon = d.expiring.total["1"];
  if (soon?.count) {
    const week = (d.expiring.total["7"]?.count ?? 0) - soon.count;
    out.push({
      id: "expiring",
      tone: "warn",
      title: `${soon.count} ${soon.count === 1 ? "подписка заканчивается" : "подписок заканчивается"} завтра`,
      sub: `${d.expiring.proxy["1"]?.count ?? 0} прокси · ${d.expiring.vpn["1"]?.count ?? 0} VPN · ещё ${week} за 2–7 дней`,
      money: `${money(soon.revenue)} на кону`,
      moneyTone: "text-amber-300",
      // Рассылка ровно этим людям (сегмент expiring:1), а не всем платникам.
      // Автонапоминания шедулера при этом работают своим чередом.
      action: { label: "Напомнить", to: "/broadcast?segment=expiring:1" },
    });
  }

  if (d.trend.falling_streak) {
    const [recent, , oldest] = d.trend.weeks;
    out.push({
      id: "trend",
      tone: "warn",
      title: "Выручка падает третью неделю",
      sub: `недельная упала с ${money(oldest)} до ${money(recent)}`,
      money: `${d.trend.change_pct.toFixed(0)}%`,
      moneyTone: "text-amber-300",
    });
  }

  return out;
}

function TodoRow({ tone, title, sub, money: m, moneyTone, action }: Todo) {
  return (
    <div
      className={`rounded-xl card-elev px-5 py-4 flex items-center gap-4 flex-wrap border-l-[3px] ${
        tone === "crit" ? "border-l-rose-400" : "border-l-amber-400"
      }`}
    >
      <span className="text-lg leading-none">{tone === "crit" ? "🔴" : "🟡"}</span>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-[15px]">{title}</div>
        <div className="text-zinc-500 text-[12.5px] mt-0.5 tabular-nums">{sub}</div>
      </div>
      {m && (
        <span className={`font-semibold tabular-nums whitespace-nowrap ${moneyTone ?? ""}`}>
          {m}
        </span>
      )}
      {action && (
        <Link
          to={action.to}
          className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/10 text-[13px] font-medium hover:border-brand hover:text-brand transition-colors whitespace-nowrap"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

function Big({
  label,
  value,
  sub,
  foot,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  foot?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-2xl card-elev p-6">
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div
        className={`text-[34px] leading-none font-bold tabular-nums tracking-tight mt-3 ${tone ?? ""}`}
      >
        {value}
      </div>
      <div className="text-zinc-500 text-[12.5px] mt-2.5 tabular-nums">{sub}</div>
      {foot && <div className="text-zinc-600 text-[11.5px] mt-1.5 tabular-nums">{foot}</div>}
    </div>
  );
}

function Small({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl card-elev px-4 py-3">
      <div className="text-[10.5px] uppercase tracking-[0.12em] text-zinc-500">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5 tabular-nums">{sub}</div>}
    </div>
  );
}

function Pot({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: string;
}) {
  return (
    <div>
      <div className="text-zinc-500 text-[13px]">{label}</div>
      <div className={`text-[22px] font-semibold tabular-nums tracking-tight mt-1 ${tone ?? ""}`}>
        {value}
      </div>
      <div className="text-zinc-500 text-[12.5px] mt-1 tabular-nums">{sub}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tone?: "sky";
}) {
  const on =
    tone === "sky"
      ? "bg-sky-400/10 text-sky-300 border-sky-400/40"
      : "bg-brand-soft text-brand border-brand/40 glow-ring";
  return (
    <button
      onClick={onClick}
      className={`h-8 px-4 rounded-lg text-sm font-medium border transition-all ${
        active ? on : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function Head({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-1 h-4 rounded-full bg-brand-grad" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
        {children}
      </span>
      {hint && <span className="ml-auto text-[12px] text-zinc-600">{hint}</span>}
    </div>
  );
}
